/**
 * The composer's `@` menu: point a message at something in the project.
 *
 * THE LAUNCHER'S LIST, NOT A SECOND ONE. What ⌘K can open is what `@` can
 * name: the shell builds that list once (`commandTargets`) and hands it down
 * through `ComposerRefs`, so the two cannot disagree about what exists. Only
 * the rows that ARE something are offered: a document, an app, a session. A
 * "New review" row is an action, and a terminal or a url is not a record an
 * agent can read.
 *
 * A LINK IN THE TEXT, not a field beside it. A message has no mentions array,
 * and the agent reads text: `[RFC 0318](LcYo…)` gives it the name and the id it
 * needs to read the thing, in the shape documents already use for a link. The
 * transcript draws it as a link that opens the thing (Markdown's `onOpenRef`).
 *
 * A CONTEXT, not a prop, because the list is the shell's and the composer is
 * mounted three levels below three different hosts. The default is empty, so a
 * composer in a test or a slot with no shell mounts and simply offers nothing.
 */

import { FileText } from 'lucide-react'
import {
  createContext,
  type RefObject,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { AppIcon } from '../../services/apps/AppIcon'
import { SessionMark } from '../../services/sessions/SessionMark'
import {
  type CommandTarget,
  rankTargets,
} from '../../shell/window/launcherTargets'

export const ComposerRefs = createContext<CommandTarget[]>([])

export function useComposerRefs(): CommandTarget[] {
  return useContext(ComposerRefs)
}

/** The `@token` being typed at `caret`, or null when none is. */
export interface RefQuery {
  query: string
  /** Offset of the `@`, which is where an insert replaces from. */
  start: number
}

/**
 * Is the caret inside an `@token`? The same rule as a comment's mention: the
 * `@` opens a word, so an email address does not open a menu on its domain,
 * and a space ends the token whether or not anything was chosen.
 */
export function activeRefQuery(text: string, caret: number): RefQuery | null {
  const upto = text.slice(0, caret)
  const at = upto.lastIndexOf('@')
  if (at < 0) return null
  const before = at === 0 ? '' : upto[at - 1]
  if (before && !/\s/.test(before)) return null
  const query = upto.slice(at + 1)
  if (/\s/.test(query)) return null
  return { query, start: at }
}

/** What `@` offers: things, not actions. */
export function referable(targets: CommandTarget[]): CommandTarget[] {
  return targets.filter(
    (t) => t.kind === 'document' || t.kind === 'app' || t.kind === 'session',
  )
}

/** A markdown link to the thing. Brackets in a title would close the label
 *  early, so they are dropped rather than escaped: the id is what resolves. */
export function refLink(t: Pick<CommandTarget, 'id' | 'title'>): string {
  return `[${refLabel(t)}](${t.id})`
}

/** Replace the `@token` ending at `caret` with a link, and say where the caret
 *  goes after it. A trailing space, so the next word is not glued to the link. */
export function insertRef(
  text: string,
  q: RefQuery,
  caret: number,
  t: Pick<CommandTarget, 'id' | 'title'>,
): { text: string; caret: number } {
  const link = `${refLink(t)} `
  const next = text.slice(0, q.start) + link + text.slice(caret)
  return { text: next, caret: q.start + link.length }
}

/**
 * A ref while it is being typed: the box shows `@title`, in blue, and the id
 * rides beside the text until send, when it becomes a markdown link. The same
 * model as a comment's mention (RFC 0292): you read the name, not the id.
 */
export interface DraftRef {
  at: number
  len: number
  id: string
  title: string
}

function refLabel(t: Pick<CommandTarget, 'id' | 'title'>): string {
  return t.title.replace(/[[\]]/g, '').trim() || t.id
}

/** Replace the `@token` ending at `caret` with `@title`, record the ref, and
 *  say where the caret goes. Refs after the insert move with the text. */
export function insertRefChip(
  text: string,
  q: RefQuery,
  caret: number,
  t: Pick<CommandTarget, 'id' | 'title'>,
  refs: DraftRef[],
): { text: string; caret: number; refs: DraftRef[] } {
  const title = refLabel(t)
  const chip = `@${title}`
  const inserted = `${chip} `
  const next = text.slice(0, q.start) + inserted + text.slice(caret)
  const shift = inserted.length - (caret - q.start)
  const kept = refs
    .filter((r) => r.at + r.len <= q.start || r.at >= caret)
    .map((r) => (r.at >= caret ? { ...r, at: r.at + shift } : r))
  kept.push({ at: q.start, len: chip.length, id: t.id, title })
  kept.sort((a, b) => a.at - b.at)
  return { text: next, caret: q.start + inserted.length, refs: kept }
}

/**
 * The refs still true after an edit from `prev` to `next`.
 *
 * Moved by the edit, never found again by title: two refs to different
 * things can share a title, and recovering by text would bind both to the
 * one that is left. The edit is the span between the common prefix and the
 * common suffix; a ref before it stays, one after it shifts, one it touches
 * is dropped, and its words stay as plain text you can see.
 */
export function reanchorRefs(
  prev: string,
  next: string,
  refs: DraftRef[],
): DraftRef[] {
  if (prev === next) return liveRefs(next, refs)
  let start = 0
  const max = Math.min(prev.length, next.length)
  while (start < max && prev[start] === next[start]) start++
  let tail = 0
  while (
    tail < max - start &&
    prev[prev.length - 1 - tail] === next[next.length - 1 - tail]
  )
    tail++
  const prevEnd = prev.length - tail
  const shift = next.length - prev.length
  const moved: DraftRef[] = []
  for (const r of refs) {
    if (r.at + r.len <= start) moved.push(r)
    else if (r.at >= prevEnd) moved.push({ ...r, at: r.at + shift })
  }
  return liveRefs(next, moved)
}

/** The refs whose words are still exactly `@title`, none overlapping. */
export function liveRefs(text: string, refs: DraftRef[]): DraftRef[] {
  const out: DraftRef[] = []
  let cursor = 0
  for (const r of [...refs].sort((a, b) => a.at - b.at)) {
    if (r.at < cursor) continue
    if (text.slice(r.at, r.at + r.len) !== `@${r.title}`) continue
    out.push(r)
    cursor = r.at + r.len
  }
  return out
}

/** What is sent: each `@title` becomes a link to what it names. */
export function refsToMarkdown(text: string, refs: DraftRef[]): string {
  let out = text
  for (const r of [...liveRefs(text, refs)].sort((a, b) => b.at - a.at)) {
    out = out.slice(0, r.at) + refLink(r) + out.slice(r.at + r.len)
  }
  return out
}

/** The draft split into plain runs and refs, for drawing the refs in blue. */
export function splitRefs(
  text: string,
  refs: DraftRef[],
): { text: string; ref: boolean; at: number }[] {
  const out: { text: string; ref: boolean; at: number }[] = []
  let cursor = 0
  for (const r of liveRefs(text, refs)) {
    if (r.at < cursor) continue
    if (r.at > cursor)
      out.push({ text: text.slice(cursor, r.at), ref: false, at: cursor })
    out.push({ text: text.slice(r.at, r.at + r.len), ref: true, at: r.at })
    cursor = r.at + r.len
  }
  if (cursor < text.length)
    out.push({ text: text.slice(cursor), ref: false, at: cursor })
  return out
}

/** Room a composer menu needs above the box (max-h-64 plus its margin). */
const MENU_ROOM = 272

/**
 * Whether a composer menu opens below the box rather than above it. On the
 * new-pane page the composer sits near the top of the window, and a menu that
 * opened up ran off it. Measured off the box the menu is drawn in, every render,
 * since the menu mounts only once something matches.
 */
export function useOpensDown<T extends HTMLElement>(): [
  RefObject<T | null>,
  boolean,
] {
  const ref = useRef<T>(null)
  const [down, setDown] = useState(false)
  useLayoutEffect(() => {
    const box = ref.current?.parentElement?.getBoundingClientRect()
    if (!box) return
    const below = window.innerHeight - box.bottom
    setDown(box.top < MENU_ROOM && below > box.top)
  })
  return [ref, down]
}

export function RefPicker({
  targets,
  query,
  onPick,
  onDismiss,
}: {
  targets: CommandTarget[]
  query: string
  onPick: (target: CommandTarget) => void
  onDismiss: () => void
}) {
  const matches = rankTargets(referable(targets), query).slice(0, 8)
  const [index, setIndex] = useState(0)
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new query starts from the top
  useEffect(() => setIndex(0), [query])
  const active = Math.min(index, Math.max(matches.length - 1, 0))
  const [ref, down] = useOpensDown<HTMLDivElement>()

  useEffect(() => {
    // Capture phase, like the `/` menu: this runs before the composer's own
    // Enter, which would otherwise send "@rfc" as the message.
    const onKey = (e: KeyboardEvent) => {
      // An IME's Enter commits the word being composed; it is not a pick.
      if (e.isComposing) return
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        onDismiss()
        return
      }
      if (matches.length === 0) return
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        e.stopPropagation()
        setIndex((i) => (i + 1) % matches.length)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        e.stopPropagation()
        setIndex((i) => (i - 1 + matches.length) % matches.length)
      } else if ((e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey) {
        // Shift+Tab is focus moving backward, and stays that.
        e.preventDefault()
        e.stopPropagation()
        const picked = matches[active]
        if (picked) onPick(picked)
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [matches, active, onPick, onDismiss])

  // Nothing matched: say nothing. An `@` that names nothing is just text, and
  // a box saying so over every email-ish word would be noise.
  if (matches.length === 0) return null

  return (
    <div
      ref={ref}
      role="listbox"
      className={`absolute ${down ? 'top-full mt-2' : 'bottom-full mb-2'} left-0 z-20 max-h-64 w-full max-w-md overflow-y-auto rounded-xl border border-border bg-background py-1 shadow-lg`}
    >
      {matches.map((t, i) => (
        <button
          key={t.id}
          type="button"
          role="option"
          aria-selected={i === active}
          // Mouse down, not click: a click lands after the textarea's blur.
          onMouseDown={(e) => {
            e.preventDefault()
            onPick(t)
          }}
          onMouseEnter={() => setIndex(i)}
          className={`flex w-full min-w-0 items-center gap-2 px-3 py-1.5 text-left text-[13px] ${
            i === active ? 'bg-foreground/10' : ''
          }`}
        >
          {/* A session carries its mark (RFC 0311), as it does in the comment
              `@` and the thread header, so you pick the one you recognise. */}
          <span className="grid size-4 shrink-0 place-items-center">
            {t.kind === 'session' ? (
              <SessionMark id={t.id} size={16} />
            ) : (
              <AppIcon
                appId={t.appId}
                size={14}
                fallback={
                  <FileText size={14} className="text-muted-foreground" />
                }
              />
            )}
          </span>
          <span className="min-w-0 truncate text-foreground">{t.title}</span>
          {t.kind === 'session' || t.hint ? (
            <span className="min-w-0 shrink-[999] truncate text-[11.5px] text-muted-foreground">
              {t.kind === 'session' ? 'session' : t.hint}
            </span>
          ) : null}
        </button>
      ))}
    </div>
  )
}
